import {
  Box,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useState } from "react";
import { SkuQualification } from "services/KnowfullyAdminClient";
import BasicActionsThreeDotsMenu from "components/ThreeDots/BasicActionsThreeDotsMenu";

// Mirrors one parent column: `id` and `label` come from the parent column definition,
// `render` reads the matching value off a single group member.
export type ColumnConfig<T> = {
  id: string;
  label: string;
  render: (qual: T) => React.ReactNode;
};

// Omitting both handlers renders the row without an actions cell.
type ChildRowProps<T extends SkuQualification> = {
  readonly child: T;
  readonly visibleColumns: ColumnConfig<T>[];
  readonly hasNotes: boolean;
  readonly onEdit?: (qual: SkuQualification) => void;
  readonly onDelete?: (qual: SkuQualification) => void;
};

const styles = {
  notesIconCell: {
    width: 50,
  },
  collapseCell: {
    paddingBottom: 0,
    paddingTop: 0,
  },
  collapseBox: {
    py: 2,
    px: 2,
  },
  notesLabel: {
    fontSize: "0.75rem",
    color: "text.secondary",
    fontWeight: "bold",
    mb: 1,
  },
  notesContent: {
    fontSize: "0.875rem",
  },
};

export default function ChildRow<T extends SkuQualification>({
  child,
  visibleColumns,
  hasNotes,
  onEdit,
  onDelete,
}: ChildRowProps<T>) {
  const [open, setOpen] = useState(false);
  const hasActions = !!onEdit || !!onDelete;
  const colSpan = visibleColumns.length + (hasActions ? 1 : 0) + (hasNotes ? 1 : 0);

  return (
    <>
      <TableRow hover>
        {hasNotes && (
          <TableCell sx={styles.notesIconCell}>
            {child.notes && (
              <IconButton size="small" onClick={() => setOpen(!open)}>
                {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            )}
          </TableCell>
        )}
        {visibleColumns.map((col) => (
          <TableCell key={col.id}>{col.render(child)}</TableCell>
        ))}
        {hasActions && (
          <TableCell>
            <BasicActionsThreeDotsMenu
              onEdit={onEdit && (() => onEdit(child))}
              onDelete={onDelete && (() => onDelete(child))}
            />
          </TableCell>
        )}
      </TableRow>
      {hasNotes && child.notes && (
        <TableRow>
          <TableCell style={styles.collapseCell} colSpan={colSpan}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={styles.collapseBox}>
                <Box sx={styles.notesLabel}>
                  Notes:
                </Box>
                <div
                  dangerouslySetInnerHTML={{ __html: child.notes }}
                  style={styles.notesContent}
                />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
